// src/components/AdminPanel.js
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import {
  RECIPE_IMAGE_SECTIONS,
  DEFAULT_RECIPE_IMAGE_SECTION,
  imagesInSection,
  getNextOrderInSection,
  renumberSectionOrders,
  reorderImageInSection,
  sortRecipeImages,
} from "../recipeImageSections";
import "../styles/admin-panel.css";

function RecipeImageSectionSelect({ value, onChange, id }) {
  return (
    <select
      id={id}
      className="ap-input ap-input-compact"
      value={value || DEFAULT_RECIPE_IMAGE_SECTION}
      onChange={(e) => onChange(e.target.value)}
    >
      {RECIPE_IMAGE_SECTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function RecipeImageUploadFields({
  items,
  onAdd,
  onRemove,
  onSectionChange,
  onReorder,
}) {
  const handleFiles = (event, section) => {
    onAdd(event.target.files, section);
    event.target.value = "";
  };

  return (
    <div className="recipe-image-sections-block">
      <p className="recipe-image-sections-intro">
        U svaku sekciju možete dodati <strong>više fotografija</strong> — odaberite
        više datoteka odjednom ili ponovno kliknite „Odaberi datoteke”.
      </p>

      {RECIPE_IMAGE_SECTIONS.map((section) => {
        const sectionItems = imagesInSection(items, section.value);

        return (
          <div key={section.value} className="recipe-image-section-field">
            <span className="recipe-image-section-title">
              {section.label}
              {sectionItems.length > 0 && (
                <span className="recipe-image-section-count">
                  ({sectionItems.length})
                </span>
              )}
            </span>
            <span className="recipe-image-section-hint">{section.hint}</span>
            <span className="recipe-image-section-multi">
              Može više slika u ovoj sekciji
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="recipe-image-file-input"
              onChange={(e) => handleFiles(e, section.value)}
            />

            {sectionItems.length > 0 && (
              <div className="recipe-image-section-items">
                {sectionItems.map((item, index) => (
                  <div key={item.id} className="recipe-image-upload-row">
                    <span className="recipe-image-upload-order">{index + 1}.</span>
                    <span className="recipe-image-upload-name">
                      {item.file?.name || item.image_url?.split("/").pop() || "Slika"}
                    </span>
                    {onSectionChange && (
                      <RecipeImageSectionSelect
                        value={item.section}
                        onChange={(nextSection) =>
                          onSectionChange(item.id, nextSection)
                        }
                      />
                    )}
                    {onReorder && (
                      <div className="recipe-image-reorder-btns">
                        <button
                          type="button"
                          className="ap-btn ghost small"
                          disabled={index === 0}
                          onClick={() => onReorder(item.id, "up")}
                          aria-label="Pomakni gore"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="ap-btn ghost small"
                          disabled={index === sectionItems.length - 1}
                          onClick={() => onReorder(item.id, "down")}
                          aria-label="Pomakni dolje"
                        >
                          ↓
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="ap-btn ghost small"
                      onClick={() => onRemove(item.id)}
                    >
                      Ukloni
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RecipeExistingImagesBySection({
  images,
  onSectionChange,
  onReorder,
  onDelete,
}) {
  if (!images?.length) {
    return (
      <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>Nema slika</div>
    );
  }

  return (
    <div className="recipe-image-existing-groups">
      {RECIPE_IMAGE_SECTIONS.map((section) => {
        const sectionImages = imagesInSection(images, section.value);
        if (!sectionImages.length) return null;

        return (
          <div key={section.value} className="recipe-image-existing-group">
            <h4 className="recipe-image-existing-group-title">
              {section.label}{" "}
              <span className="recipe-image-section-count">
                ({sectionImages.length})
              </span>
            </h4>
            <div className="recipe-image-existing-grid">
              {sectionImages.map((img, index) => (
                <div key={img.id} className="recipe-image-existing-card">
                  <span className="recipe-image-upload-order recipe-image-card-order">
                    {index + 1}
                  </span>
                  <img src={img.image_url} alt="" className="recipe-image-existing-thumb" />
                  <label className="recipe-image-section-edit">
                    <span className="recipe-image-section-edit-label">Pozicija</span>
                    <RecipeImageSectionSelect
                      value={img.section}
                      onChange={(nextSection) => onSectionChange(img.id, nextSection)}
                    />
                  </label>
                  <div className="recipe-image-reorder-btns recipe-image-card-actions">
                    <button
                      type="button"
                      className="ap-btn ghost small"
                      disabled={index === 0}
                      onClick={() => onReorder(img.id, "up")}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="ap-btn ghost small"
                      disabled={index === sectionImages.length - 1}
                      onClick={() => onReorder(img.id, "down")}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="ap-btn danger small"
                      onClick={() => onDelete(img.id, img.image_url)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminPanel() {
  const [recipes, setRecipes] = useState([]);
  const [travels, setTravels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterHighlight, setFilterHighlight] = useState("all");

  const [activeTab, setActiveTab] = useState("recipes");

  // Edit modals
  const [editRecipe, setEditRecipe] = useState(null);
  const [editTravel, setEditTravel] = useState(null);

  // New travel
  const [newTravelTitle, setNewTravelTitle] = useState("");
  const [newTravelDesc, setNewTravelDesc] = useState("");
  const [newTravelImages, setNewTravelImages] = useState([]);
  const travelImageInputRef = useRef(null);
  const [newTravelRecommendations, setNewTravelRecommendations] = useState([]);

  // New recipe
  const [newRecipeTitle, setNewRecipeTitle] = useState("");
  const [newRecipeDesc, setNewRecipeDesc] = useState("");
  const [newRecipeIngredients, setNewRecipeIngredients] = useState("");
  const [newRecipeSteps, setNewRecipeSteps] = useState("");
  const [newRecipeCat, setNewRecipeCat] = useState("");
  const [newRecipeType, setNewRecipeType] = useState("slano");
  const [newRecipeHighlight, setNewRecipeHighlight] = useState(false);
  const [newRecipeImages, setNewRecipeImages] = useState([]);

  const appendNewRecipeImages = (fileList, section) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setNewRecipeImages((prev) => {
      const startOrder = getNextOrderInSection(prev, section);
      const added = files.map((file, index) => ({
        id: `${Date.now()}-${section}-${index}-${file.name}`,
        file,
        section,
        order: startOrder + index,
      }));
      return [...prev, ...added];
    });
  };

  const removeNewRecipeImage = (id) => {
    setNewRecipeImages((prev) =>
      renumberSectionOrders(prev.filter((item) => item.id !== id))
    );
  };

  const updateNewRecipeImageSection = (id, section) => {
    setNewRecipeImages((prev) =>
      renumberSectionOrders(
        prev.map((item) => (item.id === id ? { ...item, section } : item))
      )
    );
  };

  const reorderNewRecipeImage = (id, direction) => {
    setNewRecipeImages((prev) => reorderImageInSection(prev, id, direction));
  };

  const appendEditRecipeImages = (fileList, section) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setEditRecipeNewImages((prev) => {
      const virtual = [
        ...(editRecipe?.recipe_images || []),
        ...prev,
      ];
      const startOrder = getNextOrderInSection(virtual, section);
      const added = files.map((file, index) => ({
        id: `${Date.now()}-${section}-${index}-${file.name}`,
        file,
        section,
        order: startOrder + index,
      }));
      return [...prev, ...added];
    });
  };

  const removeEditRecipeImage = (id) => {
    setEditRecipeNewImages((prev) =>
      renumberSectionOrders(prev.filter((item) => item.id !== id))
    );
  };

  const updateEditRecipeImageSection = (id, section) => {
    setEditRecipeNewImages((prev) =>
      renumberSectionOrders(
        prev.map((item) => (item.id === id ? { ...item, section } : item))
      )
    );
  };

  const reorderEditRecipeImage = (id, direction) => {
    setEditRecipeNewImages((prev) => reorderImageInSection(prev, id, direction));
  };

  const updateExistingRecipeImageSection = (imageId, section) => {
    setEditRecipe((prev) => ({
      ...prev,
      recipe_images: renumberSectionOrders(
        prev.recipe_images.map((row) =>
          row.id === imageId ? { ...row, section } : row
        )
      ),
    }));
  };

  const reorderExistingRecipeImage = (imageId, direction) => {
    setEditRecipe((prev) => ({
      ...prev,
      recipe_images: reorderImageInSection(
        prev.recipe_images,
        imageId,
        direction
      ),
    }));
  };

  const openRecipeEdit = (recipe) => {
    setEditRecipe({
      ...recipe,
      recipe_images: renumberSectionOrders(
        sortRecipeImages(recipe.recipe_images || [])
      ),
    });
    setEditRecipeNewImages([]);
  };

  const uploadRecipeImageItems = async (recipeId, imageItems, existingImages = []) => {
    const virtual = [...existingImages];

    for (const item of imageItems) {
      const file = item.file;
      const section = item.section || DEFAULT_RECIPE_IMAGE_SECTION;
      const order = item.order ?? getNextOrderInSection(virtual, section);
      const path = `${recipeId}/${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`;

      const { error: uploadErr } = await supabase.storage
        .from("recipes")
        .upload(path, file);

      if (uploadErr) {
        throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
      }

      const { data: urlData } = supabase.storage
        .from("recipes")
        .getPublicUrl(path);

      const { error: imageError } = await supabase.from("recipe_images").insert({
        recipe_id: recipeId,
        image_url: urlData.publicUrl,
        section,
        order,
      });

      if (imageError) throw imageError;

      virtual.push({ section, order });
    }
  };

  // Edit images (arrays of files to add)
  const [editRecipeNewImages, setEditRecipeNewImages] = useState([]);
  const [editTravelNewImages, setEditTravelNewImages] = useState([]);

  /* ------------------------------------------------------ */
  /* LOAD DATA */
  /* ------------------------------------------------------ */
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: recipeData, error: recipeError } = await supabase
        .from("recipes")
        .select("*, recipe_images(*)")
        .order("created_at", { ascending: false });

      const { data: travelData, error: travelError } = await supabase
        .from("travels")
        .select("*")
        .order("created_at", { ascending: false });

      // Load travel images separately if table exists
      let travelImagesMap = {};
      try {
        const { data: travelImagesData } = await supabase
          .from("travel_images")
          .select("*");
        
        if (travelImagesData) {
          travelImagesMap = travelImagesData.reduce((acc, img) => {
            if (!acc[img.travel_id]) {
              acc[img.travel_id] = [];
            }
            acc[img.travel_id].push(img);
            return acc;
          }, {});
        }
      } catch (imgErr) {
        // Table doesn't exist yet, that's okay
        console.log("travel_images table not found, using legacy image_url");
      }

      const { data: catData, error: catError } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (recipeError) throw recipeError;
      if (travelError) throw travelError;
      if (catError) throw catError;

      setRecipes(recipeData || []);
      
      // Attach travel_images to travels
      const travelsWithImages = (travelData || []).map(t => ({
        ...t,
        travel_images: travelImagesMap[t.id] || []
      }));
      setTravels(travelsWithImages);
      
      setCategories(catData || []);

      if (!newRecipeCat && catData?.length > 0) {
        setNewRecipeCat(catData[0].slug);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Greška pri učitavanju podataka: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ------------------------------------------------------ */
  /* FILTER RECIPES */
  /* ------------------------------------------------------ */
  const filteredRecipes = recipes.filter((r) => {
    const matchSearch =
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.description?.toLowerCase().includes(search.toLowerCase());

    const matchCat = filterCat === "all" ? true : r.category === filterCat;

    const matchType =
      filterType === "all" ? true : (r.taste || r.type) === filterType;

    const matchHighlight =
      filterHighlight === "all"
        ? true
        : filterHighlight === "yes"
        ? r.highlight
        : !r.highlight;

    return matchSearch && matchCat && matchType && matchHighlight;
  });

  /* ------------------------------------------------------ */
  /* DELETE */
  /* ------------------------------------------------------ */
  const deleteRecipe = async (id) => {
    if (!window.confirm("Obrisati recept?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("recipes").delete().eq("id", id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error("Error deleting recipe:", err);
      setError("Greška pri brisanju recepta: " + err.message);
      setLoading(false);
    }
  };

  const deleteTravel = async (id) => {
    if (!window.confirm("Obrisati putovanje?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("travels").delete().eq("id", id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error("Error deleting travel:", err);
      setError("Greška pri brisanju putovanja: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* DELETE RECIPE IMAGE */
  /* ------------------------------------------------------ */
  const deleteRecipeImage = async (imageId, imageUrl) => {
    if (!window.confirm("Obrisati ovu sliku?")) return;
    setLoading(true);
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from("recipe_images")
        .delete()
        .eq("id", imageId);

      if (dbError) throw dbError;

      // Extract path from URL and delete from storage
      try {
        const urlParts = imageUrl.split("/");
        const pathIndex = urlParts.findIndex((part) => part === "recipes");
        if (pathIndex !== -1) {
          const path = urlParts.slice(pathIndex + 1).join("/");
          await supabase.storage.from("recipes").remove([path]);
        }
      } catch (storageErr) {
        console.warn("Error deleting from storage:", storageErr);
      }

      await loadData();
    } catch (err) {
      console.error("Error deleting recipe image:", err);
      setError("Greška pri brisanju slike: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* DELETE TRAVEL IMAGE */
  /* ------------------------------------------------------ */
  const deleteTravelImage = async (imageId, imageUrl) => {
    if (!window.confirm("Obrisati ovu sliku?")) return;
    setLoading(true);
    try {
      // Try to delete from travel_images table if it exists
      try {
        const { error: dbError } = await supabase
          .from("travel_images")
          .delete()
          .eq("id", imageId);

        if (dbError && !dbError.message.includes("Could not find")) {
          throw dbError;
        }
      } catch (tableErr) {
        // If table doesn't exist, that's okay - might be legacy image_url
        console.log("travel_images table not available");
      }

      // Extract path from URL and delete from storage
      try {
        const urlParts = imageUrl.split("/");
        const pathIndex = urlParts.findIndex((part) => part === "travels");
        if (pathIndex !== -1) {
          const path = urlParts.slice(pathIndex + 1).join("/");
          await supabase.storage.from("travels").remove([path]);
        }
      } catch (storageErr) {
        console.warn("Error deleting from storage:", storageErr);
      }

      await loadData();
    } catch (err) {
      console.error("Error deleting travel image:", err);
      setError("Greška pri brisanju slike: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* SAVE RECIPE EDIT */
  /* ------------------------------------------------------ */
  const saveRecipeEdit = async () => {
    setLoading(true);
    try {
      // Update recipe data
      const { error } = await supabase
        .from("recipes")
        .update({
          title: editRecipe.title,
          description: editRecipe.description,
          ingredients: editRecipe.ingredients,
          steps: editRecipe.steps,
          category: editRecipe.category,
          taste: editRecipe.taste || editRecipe.type,
          highlight: editRecipe.highlight,
        })
        .eq("id", editRecipe.id);

      if (error) throw error;

      // Ažuriraj pozicije i redoslijed postojećih slika
      const normalizedImages = renumberSectionOrders(
        editRecipe.recipe_images || []
      );
      if (normalizedImages.length) {
        for (const img of normalizedImages) {
          const { error: sectionErr } = await supabase
            .from("recipe_images")
            .update({
              section: img.section || DEFAULT_RECIPE_IMAGE_SECTION,
              order: img.order ?? 1,
            })
            .eq("id", img.id);

          if (sectionErr) throw sectionErr;
        }
      }

      if (editRecipeNewImages.length > 0) {
        await uploadRecipeImageItems(
          editRecipe.id,
          editRecipeNewImages,
          normalizedImages
        );
      }

      setEditRecipeNewImages([]);
      setEditRecipe(null);
      await loadData();
    } catch (err) {
      console.error("Error saving recipe:", err);
      setError("Greška pri spremanju recepta: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* SAVE TRAVEL EDIT */
  /* ------------------------------------------------------ */
  const saveTravelEdit = async () => {
    setLoading(true);
    try {
      // Update travel data
      const { error } = await supabase
        .from("travels")
        .update({
          title: editTravel.title,
          description: editTravel.description || null,
        })
        .eq("id", editTravel.id);

      if (error) throw error;

      // Upload new images if any
      if (editTravelNewImages.length > 0) {
        // Upload first image to legacy image_url field
        const firstFile = editTravelNewImages[0];
        const path = `${editTravel.id}/${Date.now()}-${firstFile.name}`;

        const { error: uploadErr } = await supabase.storage
          .from("travels")
          .upload(path, firstFile);

        if (uploadErr) {
          throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
        }

        const { data: urlData } = await supabase.storage
          .from("travels")
          .getPublicUrl(path);

        // Update travel with first image URL (legacy support)
        const { error: updateError } = await supabase
          .from("travels")
          .update({ image_url: urlData.publicUrl })
          .eq("id", editTravel.id);

        if (updateError) throw updateError;

        // Try to insert into travel_images if table exists (for multiple images)
        if (editTravelNewImages.length > 1) {
          try {
            for (let i = 0; i < editTravelNewImages.length; i++) {
              const file = editTravelNewImages[i];
              const imgPath = `${editTravel.id}/${Date.now()}-${i}-${file.name}`;

              const { error: uploadErr2 } = await supabase.storage
                .from("travels")
                .upload(imgPath, file);

              if (!uploadErr2) {
                const { data: urlData2 } = await supabase.storage
                  .from("travels")
                  .getPublicUrl(imgPath);

                const { error: imageError } = await supabase
                  .from("travel_images")
                  .insert({
                    travel_id: editTravel.id,
                    image_url: urlData2.publicUrl,
                  });

                // If table doesn't exist, just continue (we already have image_url)
                if (imageError && !imageError.message.includes("Could not find")) {
                  console.warn("Could not insert into travel_images:", imageError);
                }
              }
            }
          } catch (err) {
            // If travel_images table doesn't exist, that's okay - we have image_url
            console.log("travel_images table not available, using legacy image_url only");
          }
        }
      }

      setEditTravelNewImages([]);
      setEditTravel(null);
      await loadData();
    } catch (err) {
      console.error("Error saving travel:", err);
      setError("Greška pri spremanju putovanja: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* CREATE TRAVEL */
  /* NOTE: If you want to add 'description' field, add it to Supabase:
   * 1. Go to Supabase Dashboard > Table Editor > travels table
   * 2. Add new column: name='description', type='text', nullable=true
   * 3. Then uncomment description fields in this component
   * ------------------------------------------------------ */
  const createTravel = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First create the travel
      const { data: createdTravel, error: insertError } = await supabase
        .from("travels")
        .insert({
          title: newTravelTitle,
          description: newTravelDesc || null,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Then upload images if provided
      if (newTravelImages.length > 0 && createdTravel) {
        // Upload first image to legacy image_url field
        const firstFile = newTravelImages[0];
        const path = `${createdTravel.id}/${Date.now()}-${firstFile.name}`;

        const { error: uploadErr } = await supabase.storage
          .from("travels")
          .upload(path, firstFile);

        if (uploadErr) {
          throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
        }

        const { data: urlData } = await supabase.storage
          .from("travels")
          .getPublicUrl(path);

        // Update travel with first image URL (legacy support)
        const { error: updateError } = await supabase
          .from("travels")
          .update({ image_url: urlData.publicUrl })
          .eq("id", createdTravel.id);

        if (updateError) throw updateError;

        // Try to insert into travel_images if table exists (for multiple images)
        if (newTravelImages.length > 1) {
          try {
            for (let i = 0; i < newTravelImages.length; i++) {
              const file = newTravelImages[i];
              const imgPath = `${createdTravel.id}/${Date.now()}-${i}-${file.name}`;

              const { error: uploadErr2 } = await supabase.storage
                .from("travels")
                .upload(imgPath, file);

              if (!uploadErr2) {
                const { data: urlData2 } = await supabase.storage
                  .from("travels")
                  .getPublicUrl(imgPath);

                const { error: imageError } = await supabase
                  .from("travel_images")
                  .insert({
                    travel_id: createdTravel.id,
                    image_url: urlData2.publicUrl,
                  });

                // If table doesn't exist, just continue (we already have image_url)
                if (imageError && !imageError.message.includes("Could not find")) {
                  console.warn("Could not insert into travel_images:", imageError);
                }
              }
            }
          } catch (err) {
            // If travel_images table doesn't exist, that's okay - we have image_url
            console.log("travel_images table not available, using legacy image_url only");
          }
        }
      }

      // Upload recommendations if provided
      if (newTravelRecommendations.length > 0 && createdTravel) {
        try {
          for (let i = 0; i < newTravelRecommendations.length; i++) {
            const rec = newTravelRecommendations[i];
            if (rec.description && rec.image) {
              // Upload recommendation image
              const recImgPath = `${createdTravel.id}/recommendations/${Date.now()}-${i}-${rec.image.name}`;
              
              const { error: uploadRecErr } = await supabase.storage
                .from("travels")
                .upload(recImgPath, rec.image);

              if (!uploadRecErr) {
                const { data: recUrlData } = await supabase.storage
                  .from("travels")
                  .getPublicUrl(recImgPath);

                // Try to insert into travel_recommendations table
                const { error: recError } = await supabase
                  .from("travel_recommendations")
                  .insert({
                    travel_id: createdTravel.id,
                    description: rec.description,
                    image_url: recUrlData.publicUrl,
                    order: i + 1,
                  });

                if (recError && !recError.message.includes("Could not find")) {
                  console.warn("Could not insert recommendation:", recError);
                }
              }
            }
          }
        } catch (err) {
          console.log("travel_recommendations table may not exist:", err);
        }
      }

      setNewTravelTitle("");
      setNewTravelDesc("");
      setNewTravelImages([]);
      setNewTravelRecommendations([]);
      if (travelImageInputRef.current) {
        travelImageInputRef.current.value = "";
      }

      await loadData();
    } catch (err) {
      console.error("Error creating travel:", err);
      setError("Greška pri kreiranju putovanja: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* CREATE RECIPE */
  /* ------------------------------------------------------ */
  const createRecipe = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First create the recipe
      const { data: createdRecipe, error: insertError } = await supabase
        .from("recipes")
        .insert({
          title: newRecipeTitle,
          description: newRecipeDesc,
          ingredients: newRecipeIngredients,
          steps: newRecipeSteps,
          category: newRecipeCat,
          taste: newRecipeType, // SLANO / SLATKO (koristi 'taste' kolonu)
          highlight: newRecipeHighlight,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (newRecipeImages.length > 0 && createdRecipe) {
        await uploadRecipeImageItems(createdRecipe.id, newRecipeImages);
      }

      setNewRecipeTitle("");
      setNewRecipeDesc("");
      setNewRecipeIngredients("");
      setNewRecipeSteps("");
      setNewRecipeCat(categories[0]?.slug || "");
      setNewRecipeType("slano");
      setNewRecipeHighlight(false);
      setNewRecipeImages([]);

      await loadData();
    } catch (err) {
      console.error("Error creating recipe:", err);
      setError("Greška pri kreiranju recepta: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------------ */

  return (
    <div className="ap-wrap">
      <h1 className="ap-title">Admin Panel</h1>

      {error && (
        <div
          className="ap-card"
          style={{
            background: "#fee",
            border: "1px solid #fcc",
            marginBottom: "20px",
          }}
        >
          <p style={{ color: "#c33", margin: 0 }}>{error}</p>
          <button
            className="ap-btn small"
            onClick={() => setError(null)}
            style={{ marginTop: "10px" }}
          >
            Zatvori
          </button>
        </div>
      )}

      {loading && (
        <div
          className="ap-card"
          style={{ textAlign: "center", marginBottom: "20px" }}
        >
          <p>Učitavanje...</p>
        </div>
      )}

      <button
        className="ap-btn ghost"
        style={{ marginBottom: "20px" }}
        onClick={async () => {
          await supabase.auth.signOut();
          localStorage.removeItem("sb_session");
          window.location.href = "/prijava";
        }}
      >
        🚪 Odjava
      </button>

      {/* TABS */}
      <div className="ap-tabs">
        <button
          className={activeTab === "recipes" ? "active" : ""}
          onClick={() => setActiveTab("recipes")}
        >
          📚 Recepti
        </button>

        <button
          className={activeTab === "travels" ? "active" : ""}
          onClick={() => setActiveTab("travels")}
        >
          🌍 Putovanja
        </button>
      </div>

      {/* ================================================ */}
      {/* RECEPTI */}
      {/* ================================================ */}
      {activeTab === "recipes" && (
        <>
          {/* SEARCH + FILTER */}
          <div className="ap-card ap-toolbar">
            <input
              className="ap-input"
              placeholder="🔍 Pretraži recepte..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* FILTER TYPE */}
            <select
              className="ap-input"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Svi tipovi</option>
              <option value="slano">Slano</option>
              <option value="slatko">Slatko</option>
            </select>

            {/* FILTER CATEGORY */}
            <select
              className="ap-input"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
            >
              <option value="all">Sve kategorije</option>

              {categories.map((c) => (
                <option value={c.slug} key={c.id}>
                  {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                </option>
              ))}
            </select>

            {/* FILTER HIGHLIGHT */}
            <select
              className="ap-input"
              value={filterHighlight}
              onChange={(e) => setFilterHighlight(e.target.value)}
            >
              <option value="all">Sve</option>
              <option value="yes">Istaknuti</option>
              <option value="no">Neistaknuti</option>
            </select>
          </div>

          {/* ADD NEW RECIPE */}
          <div className="ap-card" style={{ marginTop: "20px" }}>
            <h3>Dodaj novi recept</h3>

            <form className="ap-form" onSubmit={createRecipe}>
              {/* TYPE */}
              <label>
                Tip recepta
                <select
                  className="ap-input"
                  value={newRecipeType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setNewRecipeType(newType);
                    // Reset category to first available category of selected type
                    const availableCats = categories.filter((c) => c.type === newType);
                    if (availableCats.length > 0) {
                      setNewRecipeCat(availableCats[0].slug);
                    }
                  }}
                >
                  <option value="slano">Slano</option>
                  <option value="slatko">Slatko</option>
                </select>
              </label>

              {/* CATEGORY */}
              <label>
                Kategorija
                <select
                  className="ap-input"
                  value={newRecipeCat}
                  onChange={(e) => setNewRecipeCat(e.target.value)}
                >
                  {categories
                    .filter((c) => c.type === newRecipeType)
                    .map((c) => (
                      <option key={c.id} value={c.slug}>
                        {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Naslov recepta
                <input
                  className="ap-input"
                  value={newRecipeTitle}
                  onChange={(e) => setNewRecipeTitle(e.target.value)}
                  required
                />
              </label>

              <label>
                Opis
                <textarea
                  className="ap-input"
                  value={newRecipeDesc}
                  onChange={(e) => setNewRecipeDesc(e.target.value)}
                />
              </label>

              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={newRecipeHighlight}
                  onChange={(e) => setNewRecipeHighlight(e.target.checked)}
                />
                Istaknuto
              </label>

              <label>
                Sastojci
                <textarea
                  className="ap-input"
                  value={newRecipeIngredients}
                  onChange={(e) => setNewRecipeIngredients(e.target.value)}
                />
              </label>

              <label>
                Koraci pripreme
                <textarea
                  className="ap-input"
                  value={newRecipeSteps}
                  onChange={(e) => setNewRecipeSteps(e.target.value)}
                />
              </label>

              <div className="full recipe-image-sections-wrap">
                <span className="recipe-image-sections-label">Fotografije recepta</span>
                <RecipeImageUploadFields
                  items={newRecipeImages}
                  onAdd={appendNewRecipeImages}
                  onRemove={removeNewRecipeImage}
                  onSectionChange={updateNewRecipeImageSection}
                  onReorder={reorderNewRecipeImage}
                />
              </div>

              <button className="ap-btn">Dodaj recept</button>
            </form>
          </div>

          {/* RECIPE GRID */}
          <div className="ap-grid">
            {filteredRecipes.map((r) => (
              <div key={r.id} className="ap-card ap-item">
                <div className="thumb">
                  {r.recipe_images?.[0]?.image_url ? (
                    <img src={r.recipe_images[0].image_url} alt={r.title} />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#999" }}>
                      Nema slike
                    </div>
                  )}
                  {r.recipe_images?.length > 1 && (
                    <div style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>
                      +{r.recipe_images.length - 1}
                    </div>
                  )}
                </div>

                <h3>{r.title}</h3>
                <p className="muted">
                  {r.taste || r.type || "N/A"} • {r.category}
                </p>

                <div className="ap-flex-gap">
                  <button
                    className="ap-btn small"
                    onClick={() => openRecipeEdit(r)}
                  >
                    ✎ Uredi
                  </button>

                  <button
                    className="ap-btn ghost small"
                    onClick={async () => {
                      try {
                        const { error } = await supabase
                          .from("recipes")
                          .update({ highlight: !r.highlight })
                          .eq("id", r.id);
                        if (error) throw error;
                        await loadData();
                      } catch (err) {
                        console.error("Error toggling highlight:", err);
                        setError(
                          "Greška pri promjeni istaknutosti: " + err.message
                        );
                      }
                    }}
                  >
                    {r.highlight ? "⭐ Makni istaknuto" : "☆ Istakni"}
                  </button>

                  <button
                    className="ap-btn danger small"
                    onClick={() => deleteRecipe(r.id)}
                  >
                    ❌ Obriši
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================ */}
      {/* PUTOVANJA */}
      {/* ================================================ */}
      {activeTab === "travels" && (
        <>
          <div className="ap-card">
            <h3>Dodaj putovanje</h3>

            <form onSubmit={createTravel} className="ap-form">
              <label>
                Naslov
                <input
                  className="ap-input"
                  value={newTravelTitle}
                  onChange={(e) => setNewTravelTitle(e.target.value)}
                />
              </label>

              <label>
                Opis putovanja
                <textarea
                  className="ap-input"
                  value={newTravelDesc}
                  onChange={(e) => setNewTravelDesc(e.target.value)}
                />
              </label>

              {/* PREPORUKE RESTORANA I JELA */}
              <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "15px", fontSize: "1.1rem", color: "var(--text)" }}>
                  Preporuke restorana i jela
                </h4>
                
                {newTravelRecommendations.map((rec, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "15px",
                      marginBottom: "15px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <strong style={{ fontSize: "1rem", color: "var(--accent)" }}>
                        Preporuka {index + 1}
                      </strong>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = newTravelRecommendations.filter((_, i) => i !== index);
                          setNewTravelRecommendations(updated);
                        }}
                        style={{
                          background: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "5px 10px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                        }}
                      >
                        Obriši
                      </button>
                    </div>
                    
                    <label style={{ display: "block", marginBottom: "10px" }}>
                      Opis preporuke
                      <textarea
                        className="ap-input"
                        value={rec.description || ""}
                        onChange={(e) => {
                          const updated = [...newTravelRecommendations];
                          updated[index].description = e.target.value;
                          setNewTravelRecommendations(updated);
                        }}
                        placeholder="Npr. Odličan restoran s bezglutenskim opcijama..."
                        rows="3"
                      />
                    </label>
                    
                    <label style={{ display: "block" }}>
                      Fotografija
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const updated = [...newTravelRecommendations];
                            updated[index].image = file;
                            setNewTravelRecommendations(updated);
                          }
                        }}
                      />
                      {rec.image && (
                        <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                          Odabrano: {rec.image.name}
                        </span>
                      )}
                    </label>
                  </div>
                ))}

                {newTravelRecommendations.length < 10 && (
                  <button
                    type="button"
                    onClick={() => {
                      setNewTravelRecommendations([
                        ...newTravelRecommendations,
                        { description: "", image: null },
                      ]);
                    }}
                    style={{
                      background: "var(--accent)",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 20px",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                      fontWeight: "600",
                    }}
                  >
                    + Dodaj preporuku
                  </button>
                )}
                {newTravelRecommendations.length >= 10 && (
                  <p style={{ fontSize: "0.9rem", color: "var(--muted)", fontStyle: "italic" }}>
                    Maksimalno 10 preporuka
                  </p>
                )}
              </div>

              <label>
                Slike (može više)
                <input
                  ref={travelImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    setNewTravelImages(Array.from(e.target.files || []))
                  }
                />
                {newTravelImages.length > 0 && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px" }}>
                    Odabrano: {newTravelImages.length} slika
                  </span>
                )}
              </label>

              <button className="ap-btn">Dodaj</button>
            </form>
          </div>

          <div className="ap-grid">
            {travels.map((t) => (
              <div key={t.id} className="ap-card ap-item">
                <div className="thumb">
                  {t.travel_images?.[0]?.image_url || t.image_url ? (
                    <img src={t.travel_images?.[0]?.image_url || t.image_url} alt={t.title} />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#999" }}>
                      Nema slike
                    </div>
                  )}
                  {t.travel_images?.length > 1 && (
                    <div style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>
                      +{t.travel_images.length - 1}
                    </div>
                  )}
                </div>

                <h3>{t.title}</h3>
                {t.description && <p className="muted">{t.description}</p>}

                <div className="ap-flex-gap">
                  <button
                    className="ap-btn small"
                    onClick={() => {
                      setEditTravel(t);
                      setEditTravelNewImages([]);
                    }}
                  >
                    ✎ Uredi
                  </button>

                  <button
                    className="ap-btn danger small"
                    onClick={() => deleteTravel(t.id)}
                  >
                    ❌ Obriši
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================ */}
      {/* MODALS */}
      {/* ================================================ */}

      {/* RECIPE MODAL */}
      {editRecipe && (
        <div className="ap-modal">
          <div className="ap-modal-body ap-card">
            <div className="ap-modal-head">
              <h2>Uredi recept</h2>
              <button className="ap-x" onClick={() => setEditRecipe(null)}>
                ✕
              </button>
            </div>

            <div className="ap-form ap-grid-2">
              <label>
                Tip recepta
                <select
                  className="ap-input"
                  value={editRecipe.taste || editRecipe.type || "slano"}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      taste: e.target.value,
                      type: e.target.value,
                    })
                  }
                >
                  <option value="slano">Slano</option>
                  <option value="slatko">Slatko</option>
                </select>
              </label>

              <label>
                Kategorija
                <select
                  className="ap-input"
                  value={editRecipe.category}
                  onChange={(e) =>
                    setEditRecipe({ ...editRecipe, category: e.target.value })
                  }
                >
                  {categories
                    .filter((c) => c.type === (editRecipe.taste || editRecipe.type || "slano"))
                    .map((c) => (
                      <option key={c.id} value={c.slug}>
                        {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                      </option>
                    ))}
                </select>
              </label>

              <label className="full">
                Naslov
                <input
                  className="ap-input"
                  value={editRecipe.title}
                  onChange={(e) =>
                    setEditRecipe({ ...editRecipe, title: e.target.value })
                  }
                />
              </label>

              <label className="full">
                Opis
                <textarea
                  className="ap-input"
                  value={editRecipe.description}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      description: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Sastojci
                <textarea
                  className="ap-input"
                  value={editRecipe.ingredients}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      ingredients: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Koraci pripreme
                <textarea
                  className="ap-input"
                  value={editRecipe.steps}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      steps: e.target.value,
                    })
                  }
                />
              </label>

              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={editRecipe.highlight}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      highlight: e.target.checked,
                    })
                  }
                />
                Istaknuto
              </label>

              <div className="full">
                <span className="recipe-image-sections-label">Postojeće slike</span>
                <p className="recipe-image-section-hint recipe-image-section-hint-block">
                  Više slika po sekciji — redoslijed mijenjate strelicama ↑ ↓.
                </p>
                <RecipeExistingImagesBySection
                  images={editRecipe.recipe_images}
                  onSectionChange={updateExistingRecipeImageSection}
                  onReorder={reorderExistingRecipeImage}
                  onDelete={deleteRecipeImage}
                />
              </div>

              <div className="full recipe-image-sections-wrap">
                <span className="recipe-image-sections-label">Dodaj nove slike</span>
                <RecipeImageUploadFields
                  items={editRecipeNewImages}
                  onAdd={appendEditRecipeImages}
                  onRemove={removeEditRecipeImage}
                  onSectionChange={updateEditRecipeImageSection}
                  onReorder={reorderEditRecipeImage}
                />
              </div>
            </div>

            <div className="ap-actions">
              <button
                className="ap-btn ghost"
                onClick={() => setEditRecipe(null)}
              >
                Odustani
              </button>
              <button className="ap-btn" onClick={saveRecipeEdit}>
                Spremi promjene
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TRAVEL MODAL */}
      {editTravel && (
        <div className="ap-modal">
          <div className="ap-modal-body ap-card">
            <div className="ap-modal-head">
              <h2>Uredi putovanje</h2>
              <button className="ap-x" onClick={() => setEditTravel(null)}>
                ✕
              </button>
            </div>

            <div className="ap-form">
              <label>
                Naslov
                <input
                  className="ap-input"
                  value={editTravel.title}
                  onChange={(e) =>
                    setEditTravel({ ...editTravel, title: e.target.value })
                  }
                />
              </label>

              <label>
                Opis putovanja
                <textarea
                  className="ap-input"
                  value={editTravel.description || ""}
                  onChange={(e) =>
                    setEditTravel({
                      ...editTravel,
                      description: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Postojeće slike
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginTop: "8px" }}>
                  {editTravel.travel_images?.map((img) => (
                    <div key={img.id} style={{ position: "relative" }}>
                      <img
                        src={img.image_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid var(--border)",
                        }}
                      />
                      <button
                        type="button"
                        className="ap-btn danger small"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          padding: "4px 8px",
                          fontSize: "0.75rem",
                        }}
                        onClick={() => deleteTravelImage(img.id, img.image_url)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {editTravel.image_url && (!editTravel.travel_images || editTravel.travel_images.length === 0) && (
                    <div key="legacy" style={{ position: "relative" }}>
                      <img
                        src={editTravel.image_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid var(--border)",
                        }}
                      />
                      <div style={{ position: "absolute", bottom: "4px", left: "4px", background: "rgba(0,0,0,0.7)", color: "white", padding: "2px 6px", borderRadius: "4px", fontSize: "0.7rem" }}>
                        Stara slika
                      </div>
                    </div>
                  )}
                  {(!editTravel.travel_images || editTravel.travel_images.length === 0) && !editTravel.image_url && (
                    <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>
                      Nema slika
                    </div>
                  )}
                </div>
              </label>

              <label className="full">
                Dodaj nove slike
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    setEditTravelNewImages(Array.from(e.target.files || []))
                  }
                />
                {editTravelNewImages.length > 0 && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                    Odabrano: {editTravelNewImages.length} nova slika
                  </span>
                )}
              </label>
            </div>

            <div className="ap-actions">
              <button
                className="ap-btn ghost"
                onClick={() => setEditTravel(null)}
              >
                Odustani
              </button>
              <button className="ap-btn" onClick={saveTravelEdit}>
                Spremi promjene
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
